// Multi-entity Phase 2 — administrative entity grants.
//
// Sits on the Scope Policy page because it is the other half of the same
// question. The scope policy says "over WHICH POPULATION" in the abstract
// (self, my department, my branch, the whole tenant, or — new — specific legal
// entities). This section says WHICH entities, for whom.
//
// The distinction this screen has to make unmissable: a grant is
// ADMINISTRATIVE reach, not employment. Group HR is employed by one company
// and administers several. Nobody gets reach over their own employer by
// default, and nobody loses reach over it by being moved.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Building2, Info, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { LegalEntityLite } from '@/features/payroll/types';
import {
  listEntityGrants, createEntityGrant, revokeEntityGrant,
  type EntityGrant, type EntityGrantSubjectKind,
} from '@/services/rbacService';

interface Props {
  /** Role keys from the scope-policy response, for the subject picker. */
  roleOptions: { key: string; label: string }[];
  canEdit: boolean;
}

// The services a grant can scope. '*' is every group and is offered last,
// because it is the answer people reach for when they have not decided.
// Each value is the PERMISSION PREFIX auth matches a grant on ("employee:payment:read"
// → "employee"). The employee group was offered as 'employees', which matches no
// permission, so such a grant silently conferred nothing (DEF-0mvnja6v9001):
// the label keeps the familiar word, the value is the group auth uses.
const PERMISSION_GROUPS: { value: string; label: string }[] = [
  { value: 'payroll', label: 'payroll' },
  { value: 'employee', label: 'employees' },
  { value: 'expenses', label: 'expenses' },
  { value: 'reports', label: 'reports' },
  { value: '*', label: '*' },
];
// Grants saved under the old 'employees' name match nothing in auth.
const LEGACY_GROUPS: Record<string, string> = { employees: 'employee' };

const SUBJECT_KIND_LABELS: Record<EntityGrantSubjectKind, string> = {
  role: 'Role',
  user: 'Person',
  custom_role: 'Custom role',
  api_client: 'API client',
};

export const EntityGrantsSection: React.FC<Props> = ({ roleOptions, canEdit }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [grants, setGrants] = useState<EntityGrant[]>([]);
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [subjectKind, setSubjectKind] = useState<EntityGrantSubjectKind>('role');
  const [subjectKey, setSubjectKey] = useState('');
  const [permissionGroup, setPermissionGroup] = useState('payroll');
  const [entityId, setEntityId] = useState('');
  // The server's refusal of the last "Grant access", kept on screen next to the form
  // (the toast fades; a refused security change must not look like it went through).
  // Cleared by any edit to the form, or by the next attempt.
  const [addError, setAddError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, e] = await Promise.all([listEntityGrants(), payrollApi.listLegalEntities()]);
      setGrants(g.grants);
      setEntities(e);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('entityGrants.loadFailed', {
        defaultValue: 'Could not load entity grants.',
      })));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!subjectKey.trim() || !entityId) return;
    setSaving(true);
    setAddError(null);
    try {
      await createEntityGrant({
        subjectKind,
        subjectKey: subjectKey.trim(),
        permissionGroup,
        // 'ALL' is a UI sentinel, never sent as an entity id.
        legalEntityId: entityId === 'ALL' ? null : entityId,
        allEntities: entityId === 'ALL',
      });
      setSubjectKey('');
      setEntityId('');
      await load();
      toast.success(t('entityGrants.added', { defaultValue: 'Grant added.' }));
    } catch (err) {
      // The form keeps what was entered so the admin can correct it and retry.
      const fallback = t('entityGrants.addFailed', { defaultValue: 'Could not add the grant.' });
      toast.error(getApiErrorMessage(err, fallback));
      setAddError(getApiErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (g: EntityGrant) => {
    const where = g.allEntities
      ? t('entityGrants.allEntities', { defaultValue: 'all entities' })
      : entities.find((e) => e.id === g.legalEntityId)?.name ?? g.legalEntityId;
    const ok = await confirm({
      title: t('entityGrants.revokeTitle', { defaultValue: 'Revoke this grant?' }),
      message: t('entityGrants.revokeDesc', {
        defaultValue:
          '{{subject}} will no longer administer {{where}} for {{group}}. Anyone relying on it loses that reach immediately.',
        subject: g.subjectKey, where, group: g.permissionGroup,
      }),
      confirmLabel: t('entityGrants.revoke', { defaultValue: 'Revoke' }),
      danger: true,
    });
    if (!ok) return;
    try {
      await revokeEntityGrant(g.id);
      await load();
      toast.success(t('entityGrants.revoked', { defaultValue: 'Grant revoked.' }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('entityGrants.revokeFailed', {
        defaultValue: 'Could not revoke the grant.',
      })));
    }
  };

  const entityName = (g: EntityGrant) =>
    g.allEntities
      ? t('entityGrants.allEntities', { defaultValue: 'all entities' })
      : entities.find((e) => e.id === g.legalEntityId)?.name ?? g.legalEntityId ?? '—';

  // With only one entity there is nothing to grant: reach over "the company"
  // is what a tenant-scoped role already means.
  if (loading || entities.length < 2) return null;

  return (
    <section className="space-y-4">
      <div className="flex items-start gap-3">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="space-y-1">
          <h2 className="text-sm font-medium text-foreground">
            {t('entityGrants.title', { defaultValue: 'Entity access' })}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('entityGrants.help', {
              defaultValue:
                'Which companies a role or person may administer. This is separate from who employs them — someone employed by one company can administer several, and being moved between companies changes neither.',
            })}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {t('entityGrants.noGrantMeaning', {
            defaultValue:
              'No grant means no entity access at all — a permission scoped to Legal entity then falls back to the person’s own records. It does not mean every company.',
          })}
        </span>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 font-medium">{t('entityGrants.who', { defaultValue: 'Who' })}</th>
              <th scope="col" className="px-3 py-2 font-medium">{t('entityGrants.for', { defaultValue: 'For' })}</th>
              <th scope="col" className="px-3 py-2 font-medium">{t('entityGrants.company', { defaultValue: 'Company' })}</th>
              {canEdit && <th scope="col" className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {grants.map((g) => (
              <tr key={g.id}>
                <td className="px-3 py-2">
                  <span className="text-foreground">{g.subjectKey}</span>
                  <Badge variant="outline" className="ml-2">{SUBJECT_KIND_LABELS[g.subjectKind] ?? g.subjectKind}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {PERMISSION_GROUPS.find((p) => p.value === g.permissionGroup)?.label ?? g.permissionGroup}
                  {LEGACY_GROUPS[g.permissionGroup] && (
                    <Badge variant="outline" className="ml-2" data-testid="legacy-group">
                      {t('entityGrants.legacyGroup', {
                        defaultValue: 'No effect — revoke and grant again for employees',
                      })}
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  {g.allEntities ? (
                    // Called out, because it silently includes companies that
                    // do not exist yet.
                    <Badge variant="outline" className="border-warning/40 text-warning">{entityName(g)}</Badge>
                  ) : (
                    <span className="text-foreground">{entityName(g)}</span>
                  )}
                </td>
                {canEdit && (
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => void revoke(g)}
                      aria-label={t('entityGrants.revokeFor', { defaultValue: 'Revoke {{subject}}', subject: g.subjectKey })}>
                      <X className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {grants.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="px-3 py-6 text-center text-muted-foreground">
                  {t('entityGrants.none', {
                    defaultValue: 'No entity grants yet. Nobody has cross-company access.',
                  })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <Select value={subjectKind} onValueChange={(v) => { setSubjectKind(v as EntityGrantSubjectKind); setSubjectKey(''); setAddError(null); }}>
            <SelectTrigger className="h-9 w-[9rem]" aria-label={t('entityGrants.subjectKind', { defaultValue: 'Kind' })}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['role', 'user', 'custom_role', 'api_client'] as EntityGrantSubjectKind[]).map((k) => (
                <SelectItem key={k} value={k}>{SUBJECT_KIND_LABELS[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {subjectKind === 'role' && roleOptions.length > 0 ? (
            <Select value={subjectKey} onValueChange={(v) => { setSubjectKey(v); setAddError(null); }}>
              <SelectTrigger className="h-9 w-[14rem]" aria-label={t('entityGrants.role', { defaultValue: 'Role' })}>
                <SelectValue placeholder={t('entityGrants.pickRole', { defaultValue: 'Pick a role' })} />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r.key} value={r.key}>{r.label || r.key}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-9 w-[14rem]"
              value={subjectKey}
              onChange={(e) => { setSubjectKey(e.target.value); setAddError(null); }}
              placeholder={
                subjectKind === 'user'
                  ? t('entityGrants.userIdPh', { defaultValue: 'User ID' })
                  : t('entityGrants.keyPh', { defaultValue: 'Key' })
              }
              aria-label={t('entityGrants.subjectKey', { defaultValue: 'Who' })}
            />
          )}

          <Select value={permissionGroup} onValueChange={(v) => { setPermissionGroup(v); setAddError(null); }}>
            <SelectTrigger className="h-9 w-[10rem]" aria-label={t('entityGrants.for', { defaultValue: 'For' })}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERMISSION_GROUPS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.value === '*' ? t('entityGrants.everything', { defaultValue: 'Everything' }) : p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={entityId} onValueChange={(v) => { setEntityId(v); setAddError(null); }}>
            <SelectTrigger className="h-9 w-[14rem]" aria-label={t('entityGrants.company', { defaultValue: 'Company' })}>
              <SelectValue placeholder={t('entityGrants.pickCompany', { defaultValue: 'Pick a company' })} />
            </SelectTrigger>
            <SelectContent>
              {entities.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
              <SelectItem value="ALL">
                {t('entityGrants.allEntitiesOption', { defaultValue: 'All companies (including future ones)' })}
              </SelectItem>
            </SelectContent>
          </Select>

          <Button size="sm" disabled={saving || !subjectKey.trim() || !entityId} onClick={() => void add()}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    : <Plus className="mr-2 h-4 w-4" aria-hidden />}
            {t('entityGrants.add', { defaultValue: 'Grant access' })}
          </Button>
        </div>
      )}

      {canEdit && addError && (
        <div role="alert" className="flex items-start gap-2 rounded-md bg-danger-soft p-3 text-xs text-danger-strong">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="space-y-0.5">
            <p className="font-medium">
              {t('entityGrants.addRefused', { defaultValue: 'The grant was not added' })}
            </p>
            <p>{addError}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default EntityGrantsSection;
